import { useMemo } from "react";
import { useIntl } from "react-intl";
import { useWatch } from "react-hook-form";

import Card from "components/v2/Card/Card";
import testIds from "constants/testIds";
import { useGetFreightModesConfig } from "api/queries/location/useGetFreightModesConfig";
import { useGetServiceDetailsConfig } from "api/queries/location/useGetServiceDetailsConfig";
import type { InvitedModes, Service } from "api/models/draftRequest.model";
import { ReactComponent as CheckIcon } from "assets/images/iconsV2/check.svg";
import { ReactComponent as XIcon } from "assets/images/iconsV2/x-close.svg";
import { classNames } from "helpers/commonHelpers";
import { resolveSelectedFreightModes } from "@RequestFormV2/utils/freightModeUtils";
import { getDeliveryServiceLevelOptions } from "@RequestFormV2/sections/Schedule/utils/deliveryServiceLevelOptions";
import { getFreightModeIcon } from "../utils/freightModeIcons";
import { getServiceRequirementIcons } from "../utils/serviceTypeIcons";

function splitIntoColumns<T>(items: T[], numColumns: number): T[][] {
  const columns: T[][] = Array.from({ length: numColumns }, () => []);
  const perColumn = Math.ceil(items.length / numColumns);
  items.forEach((item, i) => {
    const col = Math.floor(i / perColumn);
    columns[Math.min(col, numColumns - 1)].push(item);
  });
  return columns;
}

export default function ReviewService() {
  const intl = useIntl();
  const invitedModes = useWatch({ name: "invited_modes" }) as InvitedModes | undefined;
  const service = useWatch({ name: "service" }) as Service | undefined;
  const location = useWatch({ name: "organizational.location" }) as string | undefined;

  const { data: freightModesConfig } = useGetFreightModesConfig(location ?? "", {
    enabled: !!location,
  });
  const { data: serviceDetailsConfig } = useGetServiceDetailsConfig(location ?? "", {
    enabled: !!location,
  });

  const availableModes = freightModesConfig?.freight_modes?.available_modes ?? [];

  const selectedModes = useMemo(
    () => resolveSelectedFreightModes(invitedModes, availableModes),
    [invitedModes, availableModes],
  );

  const requirementFieldDefs = serviceDetailsConfig?.service_options?.request_service_requirement_values;

  const allRequirements = service?.special_requirements
    ? Object.entries(service.special_requirements)
        .filter(([, req]) => req.value === true || req.value === false || req.value === "true" || req.value === "false")
        .map(([key, req]) => ({
          ...req,
          label: requirementFieldDefs?.find((f: { value: string }) => f.value === key)?.label ?? key,
        }))
    : [];

  const freightColumns = useMemo(() => splitIntoColumns(selectedModes, 3), [selectedModes]);
  const reqColumns = useMemo(() => splitIntoColumns(allRequirements, 2), [allRequirements]);

  const serviceTypeLabel =
    serviceDetailsConfig?.service_options?.service_type_options?.find(
      (opt) => opt.value.toLowerCase() === service?.service_type?.toLowerCase(),
    )?.label ?? service?.service_type;
  const serviceRequirementIcons = service?.service_type ? getServiceRequirementIcons(service.service_type) : null;
  const incotermsLabel =
    serviceDetailsConfig?.service_options?.incoterm_values?.find((opt) => opt.value === service?.incoterms)?.label ??
    service?.incoterms;

  const showInsurance = serviceDetailsConfig?.service_options?.ask_insurance !== "off";
  const serviceLevelLabel = service?.service_level
    ? getDeliveryServiceLevelOptions(intl).find((option) => option.value === service.service_level)?.label
    : undefined;

  const hasAnyContent =
    selectedModes.length > 0 ||
    service?.service_type ||
    service?.service_level ||
    service?.incoterms ||
    service?.incoterm_instructions ||
    (showInsurance && (service?.insurance === true || service?.insurance === false)) ||
    allRequirements.length > 0;

  return (
    <Card borderRadius="xl" shadow="xs" data-testid={testIds.REQUEST_FORM_REVIEW_SERVICE}>
      <Card.Header title={intl.formatMessage({ id: "request-form.service", defaultMessage: "Service" })} showBorder />
      <Card.Body>
        <div className="rfv2-review__section-content">
          {selectedModes.length > 0 && (
            <div className="rfv2-review__service_field">
              <span className="rfv2-review__field-label">
                {intl.formatMessage({
                  id: "general.freightModes",
                  defaultMessage: "Freight modes",
                })}
              </span>
              <div className="rfv2-review__freight-columns">
                {freightColumns.map((column, colIdx) => (
                  <div
                    key={colIdx}
                    className={classNames("rfv2-review__freight-column", {
                      "rfv2-review__freight-column--middle": colIdx === 1,
                    })}
                  >
                    {column.map((mode) => {
                      const Icon = getFreightModeIcon(mode.value, mode.type);
                      return (
                        <div
                          key={mode.value}
                          className={classNames("rfv2-review__freight-item", {
                            "rfv2-review__freight-item--ocean": mode.type === "ocean",
                          })}
                        >
                          <Icon className="rfv2-review__freight-item__icon" />
                          <span className="rfv2-review__freight-item__label">{mode.label}</span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}

          {service?.service_type && (
            <div className="rfv2-review__service_field">
              <span className="rfv2-review__field-label">
                {intl.formatMessage({
                  id: "request-form.service.serviceRequirement.label",
                  defaultMessage: "Service requirement",
                })}
              </span>
              <span className="rfv2-review__blue-badge">
                {serviceRequirementIcons && (
                  <span className="rfv2-review__service-type-icons">
                    <serviceRequirementIcons.origin className="rfv2-review__service-type-icon" />
                    <serviceRequirementIcons.arrow className="rfv2-review__service-type-arrow" />
                    <serviceRequirementIcons.destination className="rfv2-review__service-type-icon" />
                  </span>
                )}
                {serviceTypeLabel && <span className="rfv2-review__blue-badge__label">{serviceTypeLabel}</span>}
              </span>
            </div>
          )}

          {serviceLevelLabel && (
            <div className="rfv2-review__service_field">
              <span className="rfv2-review__field-label">
                {intl.formatMessage({
                  id: "new-request.service.serviceLevel",
                  defaultMessage: "Service level",
                })}
              </span>
              <span>{serviceLevelLabel}</span>
            </div>
          )}

          {service?.incoterms && (
            <div className="rfv2-review__service_field">
              <span className="rfv2-review__field-label">
                {intl.formatMessage({
                  id: "request-form.service.incoterms.label",
                  defaultMessage: "Incoterms",
                })}
              </span>
              <span className="rfv2-review__blue-badge">{incotermsLabel}</span>
            </div>
          )}

          {service?.incoterm_instructions && (
            <div className="rfv2-review__service_field">
              <span className="rfv2-review__field-label">
                {intl.formatMessage({
                  id: "request-form.service.incoterms.instructions.label",
                  defaultMessage: "Incoterms instructions",
                })}
              </span>
              <span className="rfv2-review__field-value">{service.incoterm_instructions}</span>
            </div>
          )}

          {showInsurance && (service?.insurance === true || service?.insurance === false) && (
            <div className="rfv2-review__service_field">
              <span className="rfv2-review__field-label">
                {intl.formatMessage({
                  id: "request-form.service.insurance.included.label",
                  defaultMessage: "Insurance included",
                })}
              </span>
              <span className="rfv2-review__gray-badge">
                {service.insurance
                  ? intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" })
                  : intl.formatMessage({ id: "general.no", defaultMessage: "No" })}
              </span>
              {service.insurance && service.insurance_instructions && (
                <span className="rfv2-review__field-value">{service.insurance_instructions}</span>
              )}
            </div>
          )}

          {allRequirements.length > 0 && (
            <div className="rfv2-review__service_field">
              <span className="rfv2-review__field-label">
                {intl.formatMessage({
                  id: "request-form.service.requirements.label",
                  defaultMessage: "Requirements",
                })}
              </span>
              <div className="rfv2-review__req-columns">
                {reqColumns.map((column, colIdx) => (
                  <div key={colIdx} className="rfv2-review__req-column">
                    {column.map((req) => {
                      const isEnabled = req.value === true || req.value === "true";
                      return (
                        <div key={req.label} className="rfv2-review__req-item">
                          {isEnabled ? (
                            <CheckIcon className={classNames("rfv2-review__req-icon", "rfv2-review__req-icon--check")} />
                          ) : (
                            <XIcon className={classNames("rfv2-review__req-icon", "rfv2-review__req-icon--x")} />
                          )}
                          <div>
                            <span className="rfv2-review__req-text">{req.label}</span>
                            {req.additional_information && (
                              <>
                                <br />
                                <span className="rfv2-review__req-comment">Comments: {req.additional_information}</span>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!hasAnyContent && (
            <span className="rfv2-review__empty">
              {intl.formatMessage({ id: "request-form.review.service.empty", defaultMessage: "No service details added" })}
            </span>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
